<admin-app-header></admin-app-header>
<app-enterprise-admin-bar></app-enterprise-admin-bar>

<div class="container-fluid py-4">
    <div class="row">
        <div class="col-12">
            <h1 class="mb-4">
                <i class="fa-solid fa-truck-moving me-2"></i>
                Migrate Parking
                <small class="text-muted" *ngIf="enterpriseContext.currentEnterprise">
                    - {{ enterpriseContext.currentEnterprise.name }}
                </small>
            </h1>
            <p class="text-muted mb-4">Move a parking from one portfolio to another. This will migrate all associated subscriptions, invoices, payments, bookings, and payouts.</p>
        </div>
    </div>

    <!-- No Enterprise Selected Warning -->
    <div *ngIf="!enterpriseContext.currentEnterpriseKey" class="alert alert-info mb-4">
        <i class="fa-solid fa-info-circle me-2"></i>
        <strong>Tip:</strong> Select an enterprise from the menu above to filter portfolios. Currently showing all {{ portfolios.length }} portfolios.
    </div>

    <div class="row">
        <!-- Left Column: Selection Form -->
        <div class="col-lg-5">
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-list-check me-2"></i>
                        Select Migration Details
                    </h5>
                </div>
                <div class="card-body">
                    <!-- Step 1: Source Portfolio -->
                    <div class="mb-4">
                        <label class="form-label">
                            <span class="step-indicator">1</span>
                            Source Portfolio
                        </label>
                        <ng-select
                            [(ngModel)]="selectedSourcePortfolio"
                            [items]="portfolios"
                            bindLabel="companyName"
                            bindValue="$key"
                            placeholder="Select the portfolio to move FROM..."
                            [searchable]="true"
                            [clearable]="true"
                            [loading]="loading"
                            (change)="onSourcePortfolioChange()"
                        >
                            <ng-template ng-option-tmp let-item="item">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span>{{ item.companyName }}</span>
                                    <small class="text-muted" *ngIf="item.denormEnterpriseKey">
                                        <i class="fa-solid fa-building me-1"></i>{{ item.denormEnterpriseKey }}
                                    </small>
                                </div>
                            </ng-template>
                        </ng-select>
                        <small class="text-muted">{{ portfolios.length }} portfolios available</small>
                    </div>

                    <!-- Step 2: Parking -->
                    <div class="mb-4">
                        <label class="form-label">
                            <span class="step-indicator">2</span>
                            Parking to Migrate
                        </label>
                        <ng-select
                            [(ngModel)]="selectedParking"
                            [items]="parkings"
                            bindLabel="title"
                            bindValue="$key"
                            placeholder="Select the parking to move..."
                            [searchable]="true"
                            [clearable]="true"
                            [disabled]="!selectedSourcePortfolio"
                            [loading]="loadingParkings"
                            (change)="onParkingChange()"
                        >
                            <ng-template ng-option-tmp let-item="item">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span>{{ item.title }}</span>
                                    <div>
                                        <span class="badge me-2" [ngClass]="getStatusBadgeClass(item.parkingStatus)">
                                            {{ item.parkingStatus }}
                                        </span>
                                        <small class="text-muted">{{ item.numberOfBays }} bays</small>
                                    </div>
                                </div>
                            </ng-template>
                        </ng-select>
                        <small class="text-muted" *ngIf="selectedSourcePortfolio">{{ parkings.length }} parkings in this portfolio</small>
                        <small class="text-muted" *ngIf="!selectedSourcePortfolio">Select a source portfolio first</small>
                    </div>

                    <!-- Step 3: Target Portfolio -->
                    <div class="mb-4">
                        <label class="form-label">
                            <span class="step-indicator">3</span>
                            Target Portfolio
                        </label>
                        <ng-select
                            [(ngModel)]="selectedTargetPortfolio"
                            [items]="targetPortfolios"
                            bindLabel="companyName"
                            bindValue="$key"
                            placeholder="Select the portfolio to move TO..."
                            [searchable]="true"
                            [clearable]="true"
                            [disabled]="!selectedParking"
                            (change)="onTargetPortfolioChange()"
                        >
                            <ng-template ng-option-tmp let-item="item">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span>{{ item.companyName }}</span>
                                    <small class="text-muted" *ngIf="item.denormEnterpriseKey">
                                        <i class="fa-solid fa-building me-1"></i>{{ item.denormEnterpriseKey }}
                                    </small>
                                </div>
                            </ng-template>
                        </ng-select>
                        <small class="text-muted" *ngIf="selectedParking">{{ targetPortfolios.length }} other portfolios available</small>
                        <small class="text-muted" *ngIf="!selectedParking">Select a parking first</small>
                    </div>

                    <!-- Load Preview Button -->
                    <div class="d-grid">
                        <button class="btn btn-primary btn-lg" (click)="loadPreview()" [disabled]="!canLoadPreview || loadingPreview">
                            <i class="fa-solid fa-search me-2" *ngIf="!loadingPreview"></i>
                            <i class="fa-solid fa-spinner fa-spin me-2" *ngIf="loadingPreview"></i>
                            {{ loadingPreview ? 'Loading Preview...' : 'Preview Migration' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Info Card -->
            <div class="card">
                <div class="card-header bg-info text-white">
                    <h6 class="mb-0">
                        <i class="fa-solid fa-info-circle me-2"></i>
                        What this tool does
                    </h6>
                </div>
                <div class="card-body">
                    <p>When you migrate a parking, the following happens:</p>
                    <ul class="small">
                        <li>The parking document is moved to the new portfolio</li>
                        <li>All <strong>subscriptions</strong> are updated with the new merchant reference</li>
                        <li>All <strong>invoices</strong> are updated (including company name on invoices)</li>
                        <li>All <strong>payments</strong> are updated with the new merchant reference</li>
                        <li>All <strong>bookings</strong> are updated</li>
                        <li>All <strong>payouts</strong> for this parking's invoices are reassigned</li>
                    </ul>
                    <p class="text-muted small mb-0">
                        <i class="fa-solid fa-clock me-1"></i>
                        Migration may take a few minutes for parkings with many subscriptions.
                    </p>
                </div>
            </div>
        </div>

        <!-- Right Column: Preview -->
        <div class="col-lg-7">
            <!-- Empty State -->
            <div class="card" *ngIf="!preview && !loadingPreview">
                <div class="card-body text-center py-5">
                    <i class="fa-solid fa-eye-slash fa-3x text-muted mb-3"></i>
                    <h5 class="text-muted">No Preview Available</h5>
                    <p class="text-muted mb-0">Select a source portfolio, parking, and target portfolio, then click "Preview Migration" to see what will be moved.</p>
                </div>
            </div>

            <!-- Loading State -->
            <div class="card" *ngIf="loadingPreview">
                <div class="card-body text-center py-5">
                    <div class="spinner-border text-primary mb-3" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <h5 class="text-muted">Loading Preview...</h5>
                    <p class="text-muted mb-0">Counting subscriptions, invoices, and other related data...</p>
                </div>
            </div>

            <!-- Preview Card -->
            <div class="card" *ngIf="preview && !loadingPreview">
                <div class="card-header bg-primary text-white">
                    <h5 class="mb-0">
                        <i class="fa-solid fa-eye me-2"></i>
                        Migration Preview
                    </h5>
                </div>
                <div class="card-body">
                    <!-- Parking Details -->
                    <div class="mb-4">
                        <h6 class="text-uppercase text-muted small mb-2">Parking to Migrate</h6>
                        <div class="d-flex justify-content-between align-items-start">
                            <div>
                                <h4 class="mb-1">{{ preview.parking.title }}</h4>
                                <p class="text-muted mb-0">
                                    <span class="badge me-2" [ngClass]="getStatusBadgeClass(preview.parking.status)">{{ preview.parking.status }}</span>
                                    {{ preview.parking.numberOfBays }} bays
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Migration Direction -->
                    <div class="migration-direction mb-4">
                        <div class="row align-items-center">
                            <div class="col-5">
                                <div class="card bg-light">
                                    <div class="card-body py-2 text-center">
                                        <small class="text-muted d-block">FROM</small>
                                        <strong>{{ preview.sourceMerchant.companyName }}</strong>
                                    </div>
                                </div>
                            </div>
                            <div class="col-2 text-center">
                                <i class="fa-solid fa-arrow-right fa-2x text-primary"></i>
                            </div>
                            <div class="col-5">
                                <div class="card bg-success text-white">
                                    <div class="card-body py-2 text-center">
                                        <small class="d-block" style="opacity: 0.8">TO</small>
                                        <strong>{{ preview.targetMerchant.companyName }}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Migration Counts -->
                    <h6 class="text-uppercase text-muted small mb-3">What Will Be Migrated</h6>
                    <div class="row g-3 mb-4">
                        <div class="col-6 col-md-4">
                            <div class="stat-card">
                                <div class="stat-icon bg-primary text-white">
                                    <i class="fa-solid fa-file-signature"></i>
                                </div>
                                <div class="stat-value">{{ preview.subscriptions.count }}</div>
                                <div class="stat-label">Subscriptions</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="stat-card">
                                <div class="stat-icon bg-success text-white">
                                    <i class="fa-solid fa-file-invoice-dollar"></i>
                                </div>
                                <div class="stat-value">{{ preview.invoices.count }}</div>
                                <div class="stat-label">Invoices</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="stat-card">
                                <div class="stat-icon bg-info text-white">
                                    <i class="fa-solid fa-credit-card"></i>
                                </div>
                                <div class="stat-value">{{ preview.payments.count }}</div>
                                <div class="stat-label">Payments</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="stat-card">
                                <div class="stat-icon bg-warning text-dark">
                                    <i class="fa-solid fa-calendar-check"></i>
                                </div>
                                <div class="stat-value">{{ preview.bookings.count }}</div>
                                <div class="stat-label">Bookings</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="stat-card">
                                <div class="stat-icon bg-secondary text-white">
                                    <i class="fa-solid fa-money-bill-transfer"></i>
                                </div>
                                <div class="stat-value">{{ preview.payouts.count }}</div>
                                <div class="stat-label">Payouts</div>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="stat-card">
                                <div class="stat-icon bg-dark text-white">
                                    <i class="fa-solid fa-rand-sign"></i>
                                </div>
                                <div class="stat-value small">{{ formatCurrency(preview.invoices.totalValueInCents) }}</div>
                                <div class="stat-label">Invoice Value</div>
                            </div>
                        </div>
                    </div>

                    <!-- Subscriptions List -->
                    <div *ngIf="preview.subscriptions.items.length > 0" class="mb-4">
                        <h6 class="text-uppercase text-muted small mb-2">
                            <i class="fa-solid fa-users me-1"></i>
                            Clients with Subscriptions
                        </h6>
                        <div class="table-responsive" style="max-height: 200px; overflow-y: auto">
                            <table class="table table-sm table-striped mb-0">
                                <thead class="table-light sticky-top">
                                    <tr>
                                        <th>Client</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let sub of preview.subscriptions.items">
                                        <td>{{ sub.clientName }}</td>
                                        <td>
                                            <span class="badge" [ngClass]="getStatusBadgeClass(sub.status)">{{ sub.status }}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Warning -->
                    <div class="alert alert-warning mb-4">
                        <i class="fa-solid fa-exclamation-triangle me-2"></i>
                        <strong>Warning:</strong> This action will update all historical records. Make sure you have selected the correct parking and target portfolio.
                    </div>

                    <!-- Execute Button -->
                    <div class="d-grid">
                        <button class="btn btn-danger btn-lg" (click)="executeMigration()" [disabled]="migrating">
                            <i class="fa-solid fa-truck-moving me-2" *ngIf="!migrating"></i>
                            <i class="fa-solid fa-spinner fa-spin me-2" *ngIf="migrating"></i>
                            {{ migrating ? 'Migrating...' : 'Execute Migration' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Loading Overlay -->
    <div class="loading-overlay" *ngIf="migrating">
        <div class="text-center">
            <div class="spinner-border text-light mb-3" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <h4 class="text-white">Migration in Progress</h4>
            <p class="text-white-50">Please wait while we update all records...</p>
        </div>
    </div>
</div>
